import React from 'react';

import style from './index.module.scss';

import clsx from 'clsx';
import { Box, BoxProps, IconButton, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLayoutShell } from 'components/layout/shell-context';
import {
  GlobalSearch,
  ProfileNav,
  TopNav,
  EditorContent,
  EditorTitle,
  ButtonTask,
} from './components';
import { usePermissions } from 'hooks/use-permissions';
import { emitterLayoutContent } from './helpers';

export { EditorContent, EditorTitle, emitterLayoutContent };

interface Classes {
  root: string;
  main: string;
}

interface Props extends BoxProps {
  className?: string;
  classes?: Partial<Classes>;
  header?: React.ReactNode;
  visible?: boolean;
  MainProps?: Partial<BoxProps>;
}

export const LayoutContent: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  classes,
  header,
  children,
  visible = true,
  MainProps,
  ...rest
}) => {
  const isAllowToViewPatients = usePermissions('isAllowToEditPatients');
  // Phase F1 (Task 5c): below md the header carries the drawer's trigger.
  const shell = useLayoutShell();

  return (
    <Box
      className={clsx(style.root, classes?.root, className)}
      {...rest}
      style={{
        ...rest.style,
        display: !visible ? 'none' : undefined,
      }}
    >
      <div className={clsx(style.header)}>
        {shell.isBelowMd && (
          <IconButton
            className={style.menuButton}
            onClick={shell.openMenu}
            aria-label="menu"
            aria-expanded={shell.isMenuOpen}
            color="primary"
          >
            <MenuIcon />
          </IconButton>
        )}
        {header}
        <div className={style.separator} />
        <TopNav />
        {isAllowToViewPatients && <GlobalSearch />}
        <Box
          className={style.task}
          sx={{
            ml: '20px',
          }}
        >
          <ButtonTask />
        </Box>
        <ProfileNav />
      </div>
      <Box {...MainProps} className={clsx(style.main, classes?.main, MainProps?.className)}>
        {children}
      </Box>
    </Box>
  );
};

interface LayoutContentBelowMdProps {
  /** The page's title in the app bar (a label key's text). */
  title: React.ReactNode;
}

/**
 * N2c (T2): a page that has no app bar by design -- a second-level settings
 * page draws its grid alone, the grid's own title as its heading, beside the
 * desktop's permanent drawer. Below md the drawer is behind the app bar's menu
 * button, so there the page is wrapped in LayoutContent (the app bar, the
 * title in it); at md and up it renders its children as they are.
 */
export const LayoutContentBelowMd: React.FC<React.PropsWithChildren<LayoutContentBelowMdProps>> = ({
  title,
  children,
}) => {
  const shell = useLayoutShell();
  if (!shell.isBelowMd) {
    return <>{children}</>;
  }
  return (
    <LayoutContent
      header={
        <Typography variant="h1" color="secondary">
          {title}
        </Typography>
      }
    >
      {children}
    </LayoutContent>
  );
};
